import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Settings as SettingsIcon, User, Crown, LogOut, ChevronRight, Edit2, Camera, MapPin, Calendar, Heart, Loader2, XCircle, Clock, CreditCard, Moon, Sun, Shield, Bell, CheckCircle2, BadgeCheck, MessageSquare, TrendingUp, BellRing, BellOff, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useAuth, API } from '../App';
import { useTheme } from '../components/ThemeProvider';
import { usePushNotifications } from '../hooks/usePushNotifications';
import axios from 'axios';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

const INTERESTS = [
  'Rejser', 'Musik', 'Film', 'Læsning', 'Sport', 'Fitness', 'Madlavning', 'Kunst',
  'Fotografering', 'Gaming', 'Natur', 'Kæledyr', 'Kaffe', 'Vin', 'Dans', 'Yoga',
  'Vandreture', 'Strand', 'Teknologi', 'Mode', 'Mad', 'Koncerter', 'Museer', 'Natteliv'
];

const Settings = () => {
  const { user, token, logout, updateUser, refreshUser } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, permission: pushPermission, subscribe: subscribePush, unsubscribe: unsubscribePush } = usePushNotifications(token);
  const [pushLoading, setPushLoading] = useState(false);
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const selfieInputRef = useRef(null);
  const [editedProfile, setEditedProfile] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    age: user?.age || '',
    gender: user?.gender || '',
    looking_for: user?.looking_for || '',
    location: user?.location || '',
    interests: user?.interests || []
  });

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Du er nu logget ud');
  };

  // Handle selfie capture for verification
  const handleSelfieCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelfiePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit verification
  const handleVerification = async () => {
    if (!selfiePreview) {
      toast.error('Tag venligst en selfie først');
      return;
    }

    setVerifying(true);
    try {
      const res = await axios.post(`${API}/verification/request`, 
        { selfie_base64: selfiePreview },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      setShowVerificationDialog(false);
      setSelfiePreview(null);
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verificering fejlede');
    } finally {
      setVerifying(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/profile`, {
        ...editedProfile,
        age: parseInt(editedProfile.age) || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateUser(res.data);
      setEditing(false);
      toast.success('Profil opdateret!');
    } catch (err) {
      toast.error('Kunne ikke opdatere profil');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelingSubscription(true);
    try {
      const res = await axios.post(`${API}/trial/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setShowCancelDialog(false);
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke annullere abonnement');
    } finally {
      setCancelingSubscription(false);
    }
  };

  const toggleInterest = (interest) => {
    if (editedProfile.interests.includes(interest)) {
      setEditedProfile({
        ...editedProfile,
        interests: editedProfile.interests.filter(i => i !== interest)
      });
    } else if (editedProfile.interests.length < 10) {
      setEditedProfile({
        ...editedProfile,
        interests: [...editedProfile.interests, interest]
      });
    }
  };

  // Check trial/subscription status
  const isOnTrial = user?.premium_via_trial || user?.trial_info?.trial_active;
  const hasActiveSubscription = user?.stripe_subscription_id;
  const subscriptionCanceling = user?.subscription_status === 'canceling';

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-6 h-6 text-primary" />
            <span className="text-lg font-display font-semibold text-foreground">Indstillinger</span>
          </div>
          {editing ? (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} className="rounded-full">
                Annuller
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="save-profile-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Gem'}
              </Button>
            </div>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="rounded-full" data-testid="edit-profile-btn">
              <Edit2 className="w-4 h-4 mr-1" />
              Rediger
            </Button>
          )}
        </div>
      </div>

      <div className="pt-20 px-4">
        <div className="max-w-lg mx-auto">
          {/* Profile Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-3xl p-6 mb-6 border border-border"
          >
            <div className="flex items-start gap-4">
              <div className="relative">
                <img
                  src={user?.photos?.[0] || 'https://via.placeholder.com/100'}
                  alt={user?.name}
                  className="w-20 h-20 rounded-2xl object-cover"
                />
                {editing && (
                  <button className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <Camera className="w-4 h-4 text-white" />
                  </button>
                )}
              </div>
              <div className="flex-1">
                {editing ? (
                  <Input
                    value={editedProfile.name}
                    onChange={(e) => setEditedProfile({ ...editedProfile, name: e.target.value })}
                    className="mb-2 rounded-xl"
                    placeholder="Dit navn"
                    data-testid="edit-name-input"
                  />
                ) : (
                  <h2 className="text-xl font-display font-bold text-foreground">{user?.name}</h2>
                )}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  {editing ? (
                    <Input
                      value={editedProfile.location}
                      onChange={(e) => setEditedProfile({ ...editedProfile, location: e.target.value })}
                      className="h-8 rounded-lg text-sm"
                      placeholder="Din lokation"
                      data-testid="edit-location-input"
                    />
                  ) : (
                    <span>{user?.location || 'Ingen lokation angivet'}</span>
                  )}
                </div>
                {user?.is_premium && (
                  <Badge className="mt-2 bg-primary/10 text-primary border-none">
                    <Crown className="w-3 h-3 mr-1" />
                    {user.premium_tier === 'basic' ? 'Basis' : user.premium_tier === 'gold' ? 'Guld' : 'Platin'} Medlem
                  </Badge>
                )}
              </div>
            </div>

            {/* Bio */}
            <div className="mt-6">
              <Label className="text-sm font-medium text-foreground">Om Mig</Label>
              {editing ? (
                <Textarea
                  value={editedProfile.bio}
                  onChange={(e) => setEditedProfile({ ...editedProfile, bio: e.target.value })}
                  className="mt-2 rounded-xl resize-none"
                  rows={3}
                  placeholder="Fortæl andre om dig selv..."
                  data-testid="edit-bio-input"
                />
              ) : (
                <p className="mt-2 text-muted-foreground text-sm">
                  {user?.bio || 'Ingen bio endnu. Tilføj en så andre kan lære dig bedre at kende!'}
                </p>
              )}
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div>
                <Label className="text-xs text-muted-foreground">Alder</Label>
                {editing ? (
                  <Input
                    type="number"
                    value={editedProfile.age}
                    onChange={(e) => setEditedProfile({ ...editedProfile, age: e.target.value })}
                    className="mt-1 h-9 rounded-lg"
                    min={18}
                    max={100}
                    data-testid="edit-age-input"
                  />
                ) : (
                  <p className="font-medium text-foreground">{user?.age || '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Køn</Label>
                {editing ? (
                  <Select value={editedProfile.gender} onValueChange={(v) => setEditedProfile({ ...editedProfile, gender: v })}>
                    <SelectTrigger className="mt-1 h-9 rounded-lg" data-testid="edit-gender-select">
                      <SelectValue placeholder="Vælg" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Mand</SelectItem>
                      <SelectItem value="female">Kvinde</SelectItem>
                      <SelectItem value="other">Andet</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="font-medium text-foreground capitalize">{user?.gender === 'male' ? 'Mand' : user?.gender === 'female' ? 'Kvinde' : user?.gender === 'other' ? 'Andet' : '-'}</p>
                )}
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Søger</Label>
                {editing ? (
                  <Select value={editedProfile.looking_for} onValueChange={(v) => setEditedProfile({ ...editedProfile, looking_for: v })}>
                    <SelectTrigger className="mt-1 h-9 rounded-lg" data-testid="edit-looking-for-select">
                      <SelectValue placeholder="Vælg" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Mænd</SelectItem>
                      <SelectItem value="female">Kvinder</SelectItem>
                      <SelectItem value="both">Alle</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="font-medium text-foreground capitalize">{user?.looking_for === 'both' ? 'Alle' : user?.looking_for === 'male' ? 'Mænd' : user?.looking_for === 'female' ? 'Kvinder' : '-'}</p>
                )}
              </div>
            </div>

            {/* Interests */}
            <div className="mt-6">
              <Label className="text-sm font-medium text-foreground">
                Interesser {editing && `(${editedProfile.interests.length}/10)`}
              </Label>
              {editing ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {INTERESTS.map((interest) => (
                    <Badge
                      key={interest}
                      variant={editedProfile.interests.includes(interest) ? 'default' : 'outline'}
                      className={`cursor-pointer px-3 py-1 rounded-full ${
                        editedProfile.interests.includes(interest)
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => toggleInterest(interest)}
                    >
                      {interest}
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 mt-2">
                  {user?.interests?.length > 0 ? (
                    user.interests.map((interest, i) => (
                      <Badge key={i} variant="secondary" className="rounded-full">
                        {interest}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">Ingen interesser tilføjet</p>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          {/* Membership Status Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-card rounded-2xl border border-border mb-4 p-4"
            data-testid="membership-section"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                user?.is_premium 
                  ? user?.premium_tier === 'platinum' 
                    ? 'bg-purple-500/10' 
                    : user?.premium_tier === 'gold' 
                      ? 'bg-amber-500/10' 
                      : 'bg-primary/10'
                  : 'bg-muted'
              }`}>
                <Crown className={`w-5 h-5 ${
                  user?.is_premium
                    ? user?.premium_tier === 'platinum'
                      ? 'text-purple-500'
                      : user?.premium_tier === 'gold'
                        ? 'text-amber-500'
                        : 'text-primary'
                    : 'text-muted-foreground'
                }`} />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-foreground">
                  {user?.is_premium 
                    ? user?.premium_tier === 'platinum' 
                      ? 'Platin Medlemskab' 
                      : user?.premium_tier === 'gold' 
                        ? 'Guld Medlemskab' 
                        : 'Basis Premium'
                    : 'Gratis Medlemskab'}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {user?.is_premium
                    ? user?.premium_via_trial
                      ? `Prøveperiode - ${user?.trial_info?.days_remaining || 0} dage tilbage`
                      : 'Aktivt abonnement'
                    : 'Opgrader for flere funktioner'}
                </p>
              </div>
            </div>
            
            {user?.is_premium && (
              <div className="bg-muted/50 rounded-xl p-3 space-y-2">
                {/* Renewal/Expiry info */}
                {user?.subscription_ends_at && (
                  <div className="flex items-center justify-between text-sm pb-2 mb-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {user?.subscription_canceled ? 'Udløber' : 'Fornyes'}
                    </span>
                    <span className="font-medium text-foreground">
                      {new Date(user.subscription_ends_at).toLocaleDateString('da-DK', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                )}
                {user?.premium_via_trial && user?.trial_info?.expires_at && (
                  <div className="flex items-center justify-between text-sm pb-2 mb-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Prøveperiode udløber
                    </span>
                    <span className="font-medium text-foreground">
                      {new Date(user.trial_info.expires_at).toLocaleDateString('da-DK', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Ubegrænsede likes</span>
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">AI Match Forslag</span>
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Se hvem der liker dig</span>
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                </div>
                {(user?.premium_tier === 'gold' || user?.premium_tier === 'platinum') && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Fortryd swipes</span>
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  </div>
                )}
                {user?.premium_tier === 'platinum' && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Ubegrænsede Super Likes</span>
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  </div>
                )}
              </div>
            )}
            
            {!user?.is_premium && (
              <div className="mt-3 space-y-2">
                <Button 
                  onClick={() => navigate('/premium?trial=true')} 
                  className="w-full rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                  data-testid="try-premium-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Prøv gratis i 3 dage
                </Button>
                <Button 
                  onClick={() => navigate('/premium')} 
                  variant="outline"
                  className="w-full rounded-full"
                  data-testid="upgrade-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Se alle planer
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Ingen binding • Annuller når som helst
                </p>
              </div>
            )}
          </motion.div>

          {/* Profile Prompts & Activity Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-2xl border border-border mb-4 overflow-hidden"
          >
            <Link
              to="/edit-prompts"
              className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">Profil-spørgsmål</h4>
                  <p className="text-sm text-muted-foreground">
                    {user?.prompts?.length || 0}/3 spørgsmål besvaret
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </Link>
            
            <div className="border-t border-border" />
            
            <Link
              to="/activity"
              className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">Aktivitet</h4>
                  <p className="text-sm text-muted-foreground">Se hvem der har set din profil</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </Link>
            
            <div className="border-t border-border" />
            
            <Link
              to="/likes"
              className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">Hvem kan lide mig</h4>
                  <p className="text-sm text-muted-foreground">Se hvem der har liket dig</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </Link>
          </motion.div>

          {/* Subscription Management - Show if on trial or has subscription */}
          {(isOnTrial || hasActiveSubscription) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card rounded-2xl p-4 mb-4 border border-border"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Abonnement</h3>
                  <p className="text-sm text-muted-foreground">
                    {isOnTrial ? 'Prøveperiode aktiv' : 'Premium medlem'}
                  </p>
                </div>
              </div>

              {isOnTrial && (
                <div className="bg-accent/10 rounded-xl p-3 mb-4">
                  <div className="flex items-center gap-2 text-accent">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {user?.trial_info?.days_remaining || 3} dag{(user?.trial_info?.days_remaining || 3) !== 1 ? 'e' : ''} tilbage af prøveperioden
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Dit kort debiteres automatisk når prøveperioden udløber
                  </p>
                </div>
              )}

              {subscriptionCanceling ? (
                <div className="bg-muted rounded-xl p-3">
                  <p className="text-sm text-muted-foreground">
                    Dit abonnement annulleres ved prøveperiodens udløb.
                  </p>
                </div>
              ) : (
                <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full rounded-full text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="cancel-subscription-btn">
                      <XCircle className="w-4 h-4 mr-2" />
                      Annuller abonnement
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-sm rounded-2xl">
                    <DialogHeader>
                      <DialogTitle className="font-display">Annuller abonnement?</DialogTitle>
                      <DialogDescription>
                        Er du sikker på, at du vil annullere dit abonnement? Du vil stadig have adgang indtil prøveperioden udløber.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 pt-4">
                      <Button
                        onClick={handleCancelSubscription}
                        disabled={cancelingSubscription}
                        variant="destructive"
                        className="w-full rounded-full"
                      >
                        {cancelingSubscription ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Ja, annuller abonnement'
                        )}
                      </Button>
                      <Button
                        onClick={() => setShowCancelDialog(false)}
                        variant="outline"
                        className="w-full rounded-full"
                      >
                        Behold abonnement
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </motion.div>
          )}

          {/* Premium Section - Only show if not premium */}
          {!user?.is_premium && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Link to="/premium">
                <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-4 mb-4 border border-primary/20 flex items-center justify-between" data-testid="upgrade-card">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">Opgrader til Premium</h3>
                      <p className="text-sm text-muted-foreground">Få ubegrænsede likes & mere</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </Link>
            </motion.div>
          )}

          {/* Menu Items */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-2xl border border-border divide-y divide-border"
          >
            {/* Push Notifications Toggle */}
            {pushSupported && (
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  {pushSubscribed ? (
                    <BellRing className="w-5 h-5 text-primary" />
                  ) : (
                    <BellOff className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <span className="font-medium text-foreground">Push notifikationer</span>
                    {pushPermission === 'denied' && (
                      <p className="text-xs text-destructive">Blokeret i browser</p>
                    )}
                  </div>
                </div>
                <Switch
                  checked={pushSubscribed}
                  disabled={pushLoading || pushPermission === 'denied'}
                  onCheckedChange={async (checked) => {
                    setPushLoading(true);
                    try {
                      if (checked) {
                        const success = await subscribePush();
                        if (success) {
                          toast.success('Push notifikationer aktiveret');
                        } else {
                          toast.error('Kunne ikke aktivere push notifikationer');
                        }
                      } else {
                        const success = await unsubscribePush();
                        if (success) {
                          toast.success('Push notifikationer deaktiveret');
                        }
                      }
                    } finally {
                      setPushLoading(false);
                    }
                  }}
                  data-testid="push-notifications-toggle"
                />
              </div>
            )}

            {/* Dark Mode Toggle */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                {darkMode ? (
                  <Moon className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <Sun className="w-5 h-5 text-muted-foreground" />
                )}
                <span className="font-medium text-foreground">Mørk tilstand</span>
              </div>
              <Switch
                checked={darkMode}
                onCheckedChange={toggleDarkMode}
                data-testid="dark-mode-toggle"
              />
            </div>

            {/* Blocked Users */}
            <Link to="/blocked-users">
              <button className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium text-foreground">Blokerede brugere</span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            </Link>

            {/* Account Settings */}
            <Link to="/account">
              <button className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium text-foreground">Kontoindstillinger</span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            </Link>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-destructive"
              data-testid="logout-btn"
            >
              <div className="flex items-center gap-3">
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Log Ud</span>
              </div>
            </button>
          </motion.div>

          <p className="text-center text-xs text-muted-foreground mt-8">
            Flirra v1.0 • Made with <Heart className="w-3 h-3 inline text-primary" /> 
          </p>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Settings;
